import { useMemo, useState } from "react";
import "./MathMenu.css";

export type MathOperation = "add" | "sub" | "mul" | "div";

export interface MathOperationSettings {
  enabled: boolean;
  level: number;
}

export interface MathSettings {
  operations: Record<MathOperation, MathOperationSettings>;
}

const OPERATION_META: Array<{
  id: MathOperation;
  label: string;
  symbol: string;
}> = [
  { id: "add", label: "Dodawanie", symbol: "+" },
  { id: "sub", label: "Odejmowanie", symbol: "−" },
  { id: "mul", label: "Mnożenie", symbol: "×" },
  { id: "div", label: "Dzielenie", symbol: "÷" },
];

const DEFAULT_SETTINGS: MathSettings = {
  operations: {
    add: { enabled: true, level: 1 },
    sub: { enabled: true, level: 1 },
    mul: { enabled: false, level: 1 },
    div: { enabled: false, level: 1 },
  },
};

interface MathMenuProps {
  onStart: (settings: MathSettings) => void;
  onBack: () => void;
}

export function MathMenu({ onStart, onBack }: MathMenuProps) {
  const [settings, setSettings] = useState<MathSettings>(DEFAULT_SETTINGS);

  const enabledCount = useMemo(
    () =>
      OPERATION_META.filter((op) => settings.operations[op.id].enabled).length,
    [settings]
  );

  const updateOperation = (
    id: MathOperation,
    patch: Partial<MathOperationSettings>
  ) => {
    setSettings((prev) => ({
      operations: {
        ...prev.operations,
        [id]: {
          ...prev.operations[id],
          ...patch,
        },
      },
    }));
  };

  return (
    <div className="MenuScreen MathMenuScreen">
      <div className="MenuCard MathMenuCard">
        <button className="MenuBack" type="button" onClick={onBack}>
          Wstecz
        </button>
        <div className="MenuTitle">Matematyka</div>
        <div className="MenuSubtitle">Wybierz działania i poziom trudności</div>
        <div className="MathMenuOps">
          {OPERATION_META.map((op) => {
            const state = settings.operations[op.id];
            return (
              <div key={op.id} className="MathMenuOp">
                <label className="MathMenuToggle">
                  <input
                    type="checkbox"
                    checked={state.enabled}
                    onChange={(event) =>
                      updateOperation(op.id, { enabled: event.target.checked })
                    }
                  />
                  <span className="MathMenuToggleText">
                    {op.label} ({op.symbol})
                  </span>
                </label>
                <div className="MathMenuLevel">
                  <input
                    type="range"
                    min={1}
                    max={10}
                    value={state.level}
                    disabled={!state.enabled}
                    onChange={(event) =>
                      updateOperation(op.id, {
                        level: Number(event.target.value),
                      })
                    }
                  />
                  <span className="MathMenuLevelValue">
                    Poziom {state.level}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <button
          className="MenuButton MathMenuStart"
          type="button"
          onClick={() => onStart(settings)}
          disabled={enabledCount === 0}
        >
          Rozpocznij quiz
        </button>
        {enabledCount === 0 && (
          <div className="MathMenuHint">Zaznacz co najmniej jedno działanie.</div>
        )}
      </div>
      <div className="MenuBackdrop" />
    </div>
  );
}
